import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { statsApi, waitingApi } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { cn } from "../lib/utils";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle2,
  Clock,
  ArrowRight,
  Wallet,
  PiggyBank,
  CreditCard,
  BarChart3,
  Activity,
  Star,
  Timer,
} from "lucide-react";

const DashboardPage = () => {
  const { user } = useAuth();
  const { formatAmount: formatAmountRaw } = useCurrency();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [projectsHealth, setProjectsHealth] = useState([]);
  const [artistPerformance, setArtistPerformance] = useState([]);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [myWaitingItems, setMyWaitingItems] = useState({ items: [], total: 0, overdue_count: 0, total_penalty: 0 });
  const [loading, setLoading] = useState(true);

  // Dashboard financial data is stored in USD
  const formatAmount = (amount) => formatAmountRaw(amount, "USD");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, healthRes, performanceRes, financialRes, waitingRes] = await Promise.all([
        statsApi.getDashboard(),
        statsApi.getProjectsHealth().catch(() => ({ data: [] })),
        statsApi.getArtistPerformance().catch(() => ({ data: [] })),
        statsApi.getFinancialSummary().catch(() => ({ data: null })),
        waitingApi.getMyItems().catch(() => ({ data: { items: [], total: 0, overdue_count: 0, total_penalty: 0 } })),
      ]);
      setStats(statsRes.data);
      setProjectsHealth(healthRes.data || []);
      setArtistPerformance(performanceRes.data || []);
      setFinancialSummary(financialRes.data);
      setMyWaitingItems(waitingRes.data || { items: [], total: 0, overdue_count: 0, total_penalty: 0 });
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Format waiting time
  const formatWaitingTime = (hours) => {
    if (hours < 24) return `${Math.round(hours)}h`;
    const days = Math.floor(hours / 24);
    const remainingHours = Math.round(hours % 24);
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  };

  const getHealthColor = (score) => {
    if (score >= 80) return "text-green-400";
    if (score >= 50) return "text-yellow-400";
    return "text-red-400";
  };

  const getHealthBg = (score) => {
    if (score >= 80) return "bg-green-500";
    if (score >= 50) return "bg-yellow-500";
    return "bg-red-500";
  };

  // Artists with deadline issues (sorted by worst first)
  const artistsWithIssues = artistPerformance
    .filter(a => a.missed_deadlines > 0 || a.total_delay_days > 0)
    .slice(0, 5);

  // Projects with health issues
  const unhealthyProjects = projectsHealth
    .filter(p => p.health_score < 80)
    .slice(0, 5);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  // Admin/Team/PM/PM Lead Dashboard
  if (user?.role === "admin" || user?.role === "team" || user?.role === "pm" || user?.role === "pm_lead") {
    return (
      <div data-testid="dashboard-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope']">Dashboard</h1>
          <p className="text-sm text-zinc-500">Financial overview and project health</p>
        </div>

        {/* Financial Overview Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-zinc-500 uppercase font-mono">Total Budget</span>
                <Wallet className="h-4 w-4 text-zinc-500" />
              </div>
              <p className="text-2xl font-bold text-zinc-50">{formatAmount(financialSummary?.total_budget || stats.total_budget)}</p>
              <p className="text-xs text-zinc-500 mt-1">
                Across {stats.total_projects || 0} projects
              </p>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-zinc-500 uppercase font-mono">Committed</span>
                <CreditCard className="h-4 w-4 text-orange-500" />
              </div>
              <p className="text-2xl font-bold text-orange-400">{formatAmount(financialSummary?.total_committed)}</p>
              <p className="text-xs text-zinc-500 mt-1">
                {financialSummary && financialSummary.total_budget > 0 
                  ? `${Math.round((financialSummary.total_committed / financialSummary.total_budget) * 100)}% of budget`
                  : 'To artists'}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-zinc-500 uppercase font-mono">Paid Out</span>
                <TrendingUp className="h-4 w-4 text-green-500" />
              </div>
              <p className="text-2xl font-bold text-green-400">{formatAmount(financialSummary?.payment_breakdown?.paid)}</p>
              <p className="text-xs text-zinc-500 mt-1">Completed payments</p>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-zinc-500 uppercase font-mono">To Pay</span>
                <Clock className="h-4 w-4 text-sky-500" />
              </div>
              <p className="text-2xl font-bold text-sky-400">
                {formatAmount((financialSummary?.payment_breakdown?.approved_to_pay || 0) + (financialSummary?.payment_breakdown?.pending || 0))}
              </p>
              <p className="text-xs text-zinc-500 mt-1">Pending payments</p>
            </CardContent>
          </Card>
        </div>

        {/* P&L Summary */}
        {financialSummary && (
          <Card className="bg-zinc-900/50 border-zinc-800 mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-zinc-50 font-['Manrope'] flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-orange-500" />
                Budget Utilization
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-400">Total Budget</span>
                  <span className="font-mono text-zinc-50">{formatAmount(financialSummary.total_budget)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-400">Committed to Tasks</span>
                  <span className="font-mono text-orange-400">- {formatAmount(financialSummary.total_committed)}</span>
                </div>
                <div className="h-px bg-zinc-800" />
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-400">Available Budget</span>
                  <span className={cn(
                    "font-mono font-bold",
                    financialSummary.available_budget >= 0 ? "text-green-400" : "text-red-400"
                  )}>
                    {formatAmount(financialSummary.available_budget)}
                  </span>
                </div>
                <Progress 
                  value={financialSummary.total_budget > 0 ? (financialSummary.total_committed / financialSummary.total_budget) * 100 : 0} 
                  className="h-2 bg-zinc-800"
                />
                <div className="flex justify-between text-xs text-zinc-500">
                  <span>0%</span>
                  <span>{financialSummary.total_budget > 0 ? Math.round((financialSummary.total_committed / financialSummary.total_budget) * 100) : 0}% utilized</span>
                  <span>100%</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Projects Health */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base font-semibold text-zinc-50 font-['Manrope'] flex items-center gap-2">
                <Activity className="h-4 w-4 text-orange-500" />
                Project Health
              </CardTitle>
              <Link to="/projects">
                <Button variant="ghost" size="sm" className="text-orange-500 hover:text-orange-400 h-8 px-2">
                  View All <ArrowRight className="ml-1 h-3 w-3" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent className="pt-0">
              {projectsHealth.length === 0 ? (
                <p className="text-zinc-500 text-sm text-center py-6">No projects yet</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-zinc-500 text-xs font-medium">Project</TableHead>
                      <TableHead className="text-zinc-500 text-xs font-medium text-center">Tasks</TableHead>
                      <TableHead className="text-zinc-500 text-xs font-medium text-center">Overdue</TableHead>
                      <TableHead className="text-zinc-500 text-xs font-medium text-right">Health</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {projectsHealth.slice(0, 6).map((project) => {
                      // Determine correct link based on source
                      const projectLink = project.source === "unified" 
                        ? `/unified-projects/${project.id}` 
                        : `/projects/${project.id}`;
                      
                      return (
                      <TableRow key={project.id} className="border-zinc-800 hover:bg-zinc-800/50">
                        <TableCell className="py-2">
                          <Link to={projectLink}>
                            <p className="text-zinc-50 text-sm font-medium hover:text-orange-500 truncate max-w-[140px]">
                              {project.name}
                            </p>
                          </Link>
                        </TableCell>
                        <TableCell className="py-2 text-center">
                          <span className="text-xs text-zinc-400">
                            {project.completed_tasks}/{project.total_tasks}
                          </span>
                        </TableCell>
                        <TableCell className="py-2 text-center">
                          {project.overdue_tasks > 0 ? (
                            <Badge variant="outline" className="bg-red-500/10 text-red-400 border-red-500/20 text-xs">
                              {project.overdue_tasks}
                            </Badge>
                          ) : (
                            <CheckCircle2 className="h-4 w-4 text-green-500 mx-auto" />
                          )}
                        </TableCell>
                        <TableCell className="py-2 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-12 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                              <div 
                                className={cn("h-full rounded-full", getHealthBg(project.health_score))}
                                style={{ width: `${project.health_score}%` }}
                              />
                            </div>
                            <span className={cn("text-xs font-mono font-medium", getHealthColor(project.health_score))}>
                              {project.health_score}%
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>
                    );})}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Artist Deadline Performance */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base font-semibold text-zinc-50 font-['Manrope'] flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-orange-500" />
                Artist Deadline Issues
              </CardTitle>
              <Link to="/artists">
                <Button variant="ghost" size="sm" className="text-orange-500 hover:text-orange-400 h-8 px-2">
                  View All <ArrowRight className="ml-1 h-3 w-3" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent className="pt-0">
              {artistsWithIssues.length === 0 ? (
                <div className="text-center py-6">
                  <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-2" />
                  <p className="text-zinc-500 text-sm">All artists on track!</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-zinc-500 text-xs font-medium">Artist</TableHead>
                      <TableHead className="text-zinc-500 text-xs font-medium text-center">Rating</TableHead>
                      <TableHead className="text-zinc-500 text-xs font-medium text-center">Missed</TableHead>
                      <TableHead className="text-zinc-500 text-xs font-medium text-right">Delay</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {artistsWithIssues.map((artist) => (
                      <TableRow 
                        key={artist.id} 
                        className="border-zinc-800 hover:bg-zinc-800/50 cursor-pointer"
                        onClick={() => {
                          // Navigate to specific overdue task if available, otherwise to tasks list
                          if (artist.first_overdue_task_id) {
                            navigate(`/tasks/${artist.first_overdue_task_id}`);
                          } else {
                            navigate(`/tasks?artist=${artist.id}`);
                          }
                        }}
                      >
                        <TableCell className="py-2">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs text-zinc-400">{artist.name.charAt(0)}</span>
                            </div>
                            <p className="text-zinc-50 text-sm font-medium truncate max-w-[100px] hover:text-orange-400">
                              {artist.name}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell className="py-2 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Star className={cn("h-3 w-3", artist.rating >= 8 ? "text-yellow-400 fill-yellow-400" : "text-zinc-600")} />
                            <span className="text-xs text-zinc-400">{artist.rating}</span>
                          </div>
                        </TableCell>
                        <TableCell className="py-2 text-center">
                          <Badge variant="outline" className="bg-red-500/10 text-red-400 border-red-500/20 text-xs">
                            {artist.missed_deadlines}
                          </Badge>
                        </TableCell>
                        <TableCell className="py-2 text-right">
                          <span className="text-xs font-mono text-red-400">
                            +{artist.total_delay_days}d
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Payment Breakdown */}
        {financialSummary && (
          <Card className="bg-zinc-900/50 border-zinc-800 mt-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-zinc-50 font-['Manrope'] flex items-center gap-2">
                <PiggyBank className="h-4 w-4 text-orange-500" />
                Payment Status Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-zinc-800/50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-zinc-500 uppercase">Pending Approval</span>
                    <Clock className="h-4 w-4 text-orange-400" />
                  </div>
                  <p className="text-xl font-bold text-orange-400 font-mono">
                    {formatAmount(financialSummary.payment_breakdown?.pending)}
                  </p>
                </div>
                <div className="p-4 bg-zinc-800/50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-zinc-500 uppercase">Approved to Pay</span>
                    <CheckCircle2 className="h-4 w-4 text-sky-400" />
                  </div>
                  <p className="text-xl font-bold text-sky-400 font-mono">
                    {formatAmount(financialSummary.payment_breakdown?.approved_to_pay)}
                  </p>
                </div>
                <div className="p-4 bg-zinc-800/50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-zinc-500 uppercase">Paid</span>
                    <DollarSign className="h-4 w-4 text-green-400" />
                  </div>
                  <p className="text-xl font-bold text-green-400 font-mono">
                    {formatAmount(financialSummary.payment_breakdown?.paid)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* My Waiting Items */}
        {myWaitingItems.total > 0 && (
          <Card className="bg-zinc-900/50 border-zinc-800 mt-6">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base font-semibold text-zinc-50 font-['Manrope'] flex items-center gap-2">
                <Timer className="h-4 w-4 text-orange-500" />
                My Waiting Items
                {myWaitingItems.overdue_count > 0 && (
                  <Badge variant="outline" className="bg-red-500/10 text-red-400 border-red-500/30 ml-2">
                    {myWaitingItems.overdue_count} overdue
                  </Badge>
                )}
              </CardTitle>
              <div className="flex items-center gap-3">
                {myWaitingItems.total_penalty > 0 && (
                  <div className="flex items-center gap-1.5 text-red-400">
                    <AlertTriangle className="h-4 w-4" />
                    <span className="text-sm font-mono">-${myWaitingItems.total_penalty.toFixed(2)}</span>
                  </div>
                )}
                <Link to="/waiting-management">
                  <Button variant="ghost" size="sm" className="text-orange-500 hover:text-orange-400 h-8 px-2">
                    View All <ArrowRight className="ml-1 h-3 w-3" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-zinc-500 text-xs font-medium">Request</TableHead>
                    <TableHead className="text-zinc-500 text-xs font-medium">Project</TableHead>
                    <TableHead className="text-zinc-500 text-xs font-medium text-center">Timer</TableHead>
                    <TableHead className="text-zinc-500 text-xs font-medium text-center">Deadline</TableHead>
                    <TableHead className="text-zinc-500 text-xs font-medium text-right">Penalty</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {myWaitingItems.items.slice(0, 5).map((item) => (
                    <TableRow 
                      key={item.id} 
                      className="border-zinc-800 hover:bg-zinc-800/50 cursor-pointer"
                      onClick={() => navigate(`/client-requests/${item.id}`)}
                    >
                      <TableCell className="py-2">
                        <div>
                          <p className="text-zinc-50 text-sm font-medium truncate max-w-[200px] hover:text-orange-400">
                            {item.title}
                          </p>
                          {item.client_name && (
                            <p className="text-xs text-zinc-500">{item.client_name}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="py-2">
                        <span className="text-sm text-zinc-400 truncate max-w-[120px] block">
                          {item.project_name || "—"}
                        </span>
                      </TableCell>
                      <TableCell className="py-2 text-center">
                        <div className={cn(
                          "inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono",
                          item.is_overdue 
                            ? "bg-red-500/20 text-red-400" 
                            : "bg-zinc-800 text-zinc-400"
                        )}>
                          <Clock className="h-3 w-3" />
                          {item.is_overdue ? (
                            <span>+{formatWaitingTime(item.overdue_hours)}</span>
                          ) : (
                            formatWaitingTime(item.waiting_hours)
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="py-2 text-center">
                        <span className="text-xs text-zinc-500">
                          {new Date(item.deadline).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        </span>
                      </TableCell>
                      <TableCell className="py-2 text-right">
                        {item.penalty_amount > 0 ? (
                          <span className="text-xs font-mono text-red-400">
                            -${item.penalty_amount.toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-xs text-zinc-600">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Artist Dashboard
  if (user?.role === "artist") {
    return (
      <div data-testid="dashboard-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope']">Welcome, {user?.name}</h1>
          <p className="text-sm text-zinc-500">Your tasks and earnings overview</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <p className="text-xs text-zinc-500 uppercase font-mono">My Tasks</p>
              <p className="text-2xl font-bold text-zinc-50 mt-1">{stats.my_tasks || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <p className="text-xs text-zinc-500 uppercase font-mono">In Progress</p>
              <p className="text-2xl font-bold text-sky-400 mt-1">{stats.in_progress || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <p className="text-xs text-zinc-500 uppercase font-mono">Earned</p>
              <p className="text-2xl font-bold text-green-400 mt-1">{formatAmount(stats.total_earned)}</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <p className="text-xs text-zinc-500 uppercase font-mono">Pending</p>
              <p className="text-2xl font-bold text-orange-400 mt-1">{formatAmount(stats.pending_payment)}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Client Dashboard
  return (
    <div data-testid="dashboard-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope']">Welcome, {user?.name}</h1>
        <p className="text-sm text-zinc-500">Your projects overview</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-500 uppercase font-mono">My Projects</p>
            <p className="text-2xl font-bold text-zinc-50 mt-1">{stats.my_projects || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-500 uppercase font-mono">Approved Deliverables</p>
            <p className="text-2xl font-bold text-green-400 mt-1">{stats.approved_deliverables || 0}</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
